import {
  APIChartWidgetAttributes,
  APIDashboardFilterParameter,
  ArdoqId,
  DashboardFilterValues,
  HexColor,
} from '@ardoq/api-types';
import { Locale } from '@ardoq/locale';
import {
  GetComponentLinkHandler,
  HasComponentReadPermission,
} from '@ardoq/report-reader';
import { EnhancedScopeData } from '@ardoq/data-model';
import {
  ContextDashboardIsUsedIn,
  DashboardModes,
  OnWidgetDrilldown,
  OpenFullscreenWidget,
  ReportFromSelectedWidgetId,
} from '../types';
import {
  UsageMetricsModeWidgetProps,
  ViewModeChartWidgetProps,
} from './widgetPropTypes';
import { getCommonWidgetProps } from './getCommonWidgetProps';
import { getViewModeHeaderBarProps } from './atoms/getViewModeHeaderBarProps';
import { widgetOperations } from './operations';
import type { I18nLocale } from '@ardoq/i18n';
import { getTranslatedWidgetError } from './getTranslatedWidgetError';

export const getViewModeChartWidgetProps = ({
  dashboardId,
  widgetData,
  userLocale,
  colors,
  getHasComponentReadPermission,
  getComponentLinkHandler,
  getReportNameById,
  onDrilldown,
  contextDashboardIsUsedIn,
  openFullscreenWidget,
  dashboardFilterValues,
  dashboardFilterParameters,
  i18nLocale,
}: {
  widgetData: APIChartWidgetAttributes;
  dashboardId: ArdoqId;
  userLocale: Locale;
  colors: Record<string, HexColor>;
  getComponentLinkHandler?: GetComponentLinkHandler;
  getHasComponentReadPermission?: (
    scopeData: EnhancedScopeData
  ) => HasComponentReadPermission;
  dashboardFilterValues: DashboardFilterValues;
  dashboardFilterParameters: APIDashboardFilterParameter[];
  getReportNameById: ReportFromSelectedWidgetId | undefined;
  onDrilldown: OnWidgetDrilldown | undefined;
  contextDashboardIsUsedIn: ContextDashboardIsUsedIn;
  openFullscreenWidget: OpenFullscreenWidget | undefined;
  i18nLocale: I18nLocale;
}): ViewModeChartWidgetProps => {
  const isInPresentation = contextDashboardIsUsedIn === 'presentation';
  const translatedWidgetError = getTranslatedWidgetError(
    widgetData,
    i18nLocale
  );
  const commonWidgetProps = getCommonWidgetProps({
    dashboardId,
    isLoading: false, // no widget loading in view mode
    widgetData,
    userLocale,
    dashboardMode: DashboardModes.VIEW,
    colors,
    onDrilldown,
    getHasComponentReadPermission,
    getComponentLinkHandler,
    isSelected: false, // no widget selection in view mode
    dashboardFilterValues,
    dashboardFilterParameters,
    viewValidationErrors: translatedWidgetError ? [translatedWidgetError] : [],
    configurationInstructions: [],
    confidentialFieldLabelsByName: {},
    i18nLocale,
  });

  return {
    ...commonWidgetProps,
    headerBarProps: getViewModeHeaderBarProps({
      error: translatedWidgetError,
      widgetType: widgetData.viewType,
      name: widgetData.name,
      canExploreDatasource:
        widgetData.permissions?.canExploreDatasource ?? false,
      datasource: widgetData.datasource,
      filters: {
        ...(widgetData.filters ?? {}),
        ...(widgetOperations.isPercentageChartData(widgetData)
          ? (widgetData.condition ?? {})
          : {}),
      },
      getReportNameById,
      onDrilldown,
      contextDashboardIsUsedIn,
      openFullscreenWidget: openFullscreenWidget
        ? () => openFullscreenWidget(widgetData, dashboardFilterValues)
        : undefined,
      linkedDashboardFilters: widgetOperations.getWidgetLinkedDashboardFilters({
        widgetData,
        globalFilterValues: dashboardFilterValues,
        dashboardFilterParameters,
      }),
      isInPresentation,
      canOpenFullscreenWidget:
        commonWidgetProps.isValidWidgetData &&
        !widgetOperations.isTableWidgetData(widgetData),
      i18nLocale,
    }),
    dashboardMode: DashboardModes.VIEW,
  };
};

export const getUsageMetricsModeWidgetProps = ({
  widgetData,
  userLocale,
  colors,
  dashboardId,
  i18nLocale,
}: {
  widgetData: APIChartWidgetAttributes;
  dashboardId: ArdoqId;
  userLocale: Locale;
  colors: Record<string, HexColor>;
  i18nLocale: I18nLocale;
}): UsageMetricsModeWidgetProps => {
  return {
    ...getCommonWidgetProps({
      dashboardId,
      widgetData,
      userLocale,
      colors,

      configurationInstructions: [],
      onDrilldown: undefined,
      getHasComponentReadPermission: undefined,
      getComponentLinkHandler: undefined,
      isSelected: false,
      viewValidationErrors: [],
      dashboardFilterValues: {},
      dashboardFilterParameters: [],
      isLoading: false,
      dashboardMode: DashboardModes.METRICS,
      confidentialFieldLabelsByName: {},
      i18nLocale,
    }),
    headerBarProps: {
      name: widgetData.name,
      infoMessage: widgetData.infoMessage,
    },
    dashboardMode: DashboardModes.METRICS,
  };
};
